/**
 * Presentation helpers for unifying unit + weight display.
 *
 * Numeric values (price-per-weight-unit) come from the backend — see
 * `ProductResponseDto.pricePerWeightUnit` and `OrderItem.pricePerWeightUnit`.
 * This module owns string composition (units breakdown, total weight) and
 * locale-aware currency formatting only.
 */

import { withIva } from "@/lib/tax"

export interface UnitsLabels {
  /** Singular: "unit" / "unidad" */
  unit: string
  /** Plural: "units" / "unidades" */
  units: string
  /** Connector: "of" / "de" (e.g. "2 units of 25kg each") */
  of: string
  /** Suffix when qty > 1: "each" / "c/u". Empty string is OK. */
  each: string
  /** Suffix label for total weight: "total". Empty string is OK. */
  total: string
}

/**
 * Formats a monetary amount with a leading currency symbol and exactly two
 * decimals. Centralizes the `$${x.toFixed(2)}` / `toLocaleString(...)` patterns
 * that were duplicated across cart, checkout, order, and vendor views.
 *
 * - `grouped: false` (default) → "$1234.50" (matches the old `toFixed(2)` sites).
 * - `grouped: true` → "$1,234.50" (matches the old `toLocaleString` sites).
 *
 * Non-finite/missing input formats as "$0.00" so callers can drop ad-hoc
 * `?? 0` / `|| 0` guards.
 */
export const formatCurrency = (
  value: number | null | undefined,
  options: { grouped?: boolean; currency?: string } = {}
): string => {
  const { grouped = false, currency = "$" } = options
  const v = typeof value === "number" && Number.isFinite(value) ? value : 0
  const body = grouped
    ? v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : v.toFixed(2)
  return `${currency}${body}`
}

const formatWeightNumber = (value: number): string =>
  Number.isFinite(value)
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      })
    : "0"

/**
 * Builds "N units of Xkg each" / "1 unit of 25kg" composition strings.
 * Returns just "N units" when weight is missing/zero.
 */
export const formatUnitsBreakdown = (
  quantity: number,
  weight: number | undefined,
  weightUnit: string | undefined,
  labels: UnitsLabels
): string => {
  const qty = Math.max(0, Math.floor(quantity || 0))
  const unitWord = qty === 1 ? labels.unit : labels.units
  const w = weight && weight > 0 ? `${formatWeightNumber(weight)}${weightUnit || "kg"}` : ""
  if (!w) return `${qty} ${unitWord}`
  const suffix = qty > 1 && labels.each ? ` ${labels.each}` : ""
  return `${qty} ${unitWord} ${labels.of} ${w}${suffix}`
}

/**
 * "50 kg total" / "40 lb total" given qty × unit-weight.
 * If `labels.total` is empty, returns just the number with unit.
 */
export const formatTotalWeight = (
  quantity: number,
  weight: number | undefined,
  weightUnit: string | undefined,
  totalLabel?: string
): string => {
  const qty = Math.max(0, Math.floor(quantity || 0))
  const w = weight ?? 0
  const total = qty * w
  const base = `${formatWeightNumber(total)} ${weightUnit || "kg"}`
  return totalLabel ? `${base} ${totalLabel}` : base
}

/** "25kg/unit" subtext for inventory/catalog tiles. */
export const formatPerUnitWeight = (
  weight: number | undefined,
  weightUnit: string | undefined,
  unitLabel: string
): string => {
  if (!weight || weight <= 0) return ""
  return `${formatWeightNumber(weight)}${weightUnit || "kg"}/${unitLabel}`
}

/**
 * "$0.50/lb" — formats per-weight-unit price (which the backend computes).
 * Applies 16% IVA when withTax is requested for a taxable line.
 */
export const formatPricePerWeightUnit = (
  pricePerWeightUnit: number | undefined,
  weightUnit: string | undefined,
  options: { withTax?: boolean; isTaxable?: boolean; currency?: string } = {}
): string => {
  const { currency = "$", withTax = false, isTaxable = false } = options
  const base = pricePerWeightUnit ?? 0
  const value = withTax && isTaxable ? withIva(base) : base
  return `${currency}${value.toFixed(2)}/${weightUnit || "kg"}`
}

/**
 * Fallback derivation when DTO lacks `pricePerWeightUnit` (e.g. cart items
 * captured before the backend sync, or order items in legacy data).
 */
export const derivePricePerWeightUnit = (
  price: number | undefined,
  weight: number | undefined
): number => {
  if (!price || !weight || weight <= 0) return 0
  return price / weight
}

interface PriceWeightLike {
  price?: number | null
  weight?: number | null
  pricePerWeightUnit?: number | null
}

/**
 * Prefers the backend-computed value; falls back to `price / weight` so the
 * UI keeps working before the api-client sync lands.
 */
export const getPricePerWeightUnit = (item: PriceWeightLike): number => {
  if (item.pricePerWeightUnit && item.pricePerWeightUnit > 0) return item.pricePerWeightUnit
  return derivePricePerWeightUnit(item.price ?? undefined, item.weight ?? undefined)
}
